import { observable, action, computed, set, get, toJS } from 'mobx';
import { persist } from 'mobx-persist';
import gql from 'graphql-tag';
import ApolloClient from 'apollo-client';
import { HttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import localForage from 'localforage';
import Cookie from 'mobx-cookie';
import { AppUserDataSchema } from './app.userdataschema';

const InitialQuery = gql`
    query InitialQuery($URLID: String!) {
        readSubmission(URLID: $URLID) {
            URLID
            IsApartment
            Era
            Zone
            FranklinCouncil
            ConsentZone
            ConsentZoneCompliant
            Retrofitted2009
            Standards2009Compliant
            RValueEra
            ResultCalculator
            AssumedExternalTemperature
            Stairwells
            HomeDetails
            FloorArea
            Rooms
            PriorV2Submission
            Error
        }
    }
`,
    emailLinkMutation = gql`
        mutation EmailMutation(
            $URLID: String!
            $Email: String!
            $Link: String!
            $SecurityID: String!
        ) {
            toolEmailMutation(
                URLID: $URLID
                Email: $Email
                Link: $Link
                SecurityID: $SecurityID
            ) {
                URLID
                Error
            }
        }
    `,
    RatingMutation = gql`
        mutation RatingMutation(
            $Rating: String!
            $Comments: String!
        ) {
            ratingMutation(
                Rating: $Rating
                Comments: $Comments
            ) {
                Rating
                Comments
                Error
            }
        }
    `;

export class AppStore {
    static instance: AppStore;

    @persist('list') @observable started = []; // an array of one boolean
    @persist('object', AppUserDataSchema) @observable userData = {};
    @persist('list') @observable councils = [];
    @persist @observable submissionID = 0; // get from localstorage
    @persist @observable dataLoaded = false;
    @persist @observable era = null;
    @persist @observable zone = null;
    @persist @observable consentZone = null;
    @persist @observable consentZoneCompliant = null;
    @persist @observable retrofitted2009 = null;
    @persist @observable standards2009Compliant = null;
    @persist @observable rValueEra = null;
    @persist @observable assumedExternalTemperature = 0;
    @persist @observable stairwells = 0;

    @observable error = false;
    @observable loading = false;
    @observable emailLinkError = false;
    @observable emailLinkLoading = false;
    @observable emailLinkSuccess = false;
    @observable ratingError = false;
    @observable ratingSubmitted = new Cookie('ht-rating-submitted');
    @observable ratingValue = false;
    @observable ratingComments = false;
    @observable priorV2Submission = false;
    @observable captchaVerificationError = false;
    @observable captchaToken = false;
    @observable isCaptchaVerified = false;
    @observable captchaSiteKey = false;

    sectoken = '';
    baseURL;
    helpURL = '/';
    healthyHomesURL = '/';
    baseTitle;
    Link; // app path, ending in a /
    enableRecaptcha;

    constructor() {
        if (typeof window.HTContent !== typeof undefined &&
            typeof window.HTContent.HelpURL !== typeof undefined) {
            this.helpURL = window.HTContent.HelpURL;
            this.healthyHomesURL = window.HTContent.HealthyHomesURL;
            this.enableRecaptcha = window.HTContent.EnableRecaptcha;
            this.captchaSiteKey = window.HTContent.CaptchaSiteKey;
        }
    }

    @action reset(callback) {
        return localForage.setItem('app', {}).then(() => {
            set(this.userData, new AppUserDataSchema());
            this.submissionID = 0;
            this.era = null;
            this.zone = null;
            this.consentZone = null;
            this.consentZoneCompliant = null;
            this.standards2009Compliant = false;
            this.retrofitted2009 = null;
            this.rValueEra = false;
            this.stairwells = 0;
            this.assumedExternalTemperature = 0;
            this.priorV2Submission = false;
            this.error = false;

            if (callback) {
                callback();
            }
        });
    }

    @action clearError() {
        this.error = false;
    }

    // example, might not be needed
    @action appStarted() {
        this.started.push(true);
    }

    @action setStairwells(num) {
        this.stairwells = num;
    }

    @action setCouncils(councils) {
        this.councils = councils;
    }

    @action setEra(era) {
        this.era = era;
    }

    @action setZone(zone) {
        this.zone = zone;
    }

    @action setConsentZone(consentZone) {
        this.consentZone = consentZone;
    }

    @action setConsentZoneCompliant(consentZoneCompliant) {
        this.consentZoneCompliant = consentZoneCompliant;
    }

    @action setRetrofitted2009(retrofitted2009) {
        this.retrofitted2009 = retrofitted2009;
    }

    @action setStandards2009Compliant(standards2009Compliant) {
        this.standards2009Compliant = standards2009Compliant;
    }

    @action setRValueEra(rValueEra) {
        this.rValueEra = rValueEra;
    }

    @action setAssumedExternalTemperature(temp) {
        this.assumedExternalTemperature = temp;
    }

    // updated to push each form piece into state. Replace by key.
    // note this might need to be adjusted for the multilevel walls section
    @action updateUserData(data, sectionKey, roomNum = false) {
        const newData = {};
        if (roomNum) {
            const roomKey = `room${roomNum}`;
            this.createRoom(roomKey);
            newData.rooms = toJS(get(this.userData, 'rooms'));
            newData.rooms[roomKey][sectionKey] = data;
            set(this.userData, newData);
        } else {
            newData[sectionKey] = data;
            set(this.userData, newData);
        }
    }

    @action createRoom(roomNum) {
        const roomKey = Number.isInteger(roomNum) ? `room${roomNum}` : roomNum,
            newRoom = {};
        let current = toJS(get(this.userData, 'rooms'));

        if (typeof current === typeof undefined || Object.keys(current).length < 1) {
            newRoom.rooms = {
                room1: {}
            };
            set(this.userData, newRoom);
            current = toJS(get(this.userData, 'rooms'));
        }

        if (typeof current[roomKey] === typeof undefined || Object.keys(current[roomKey]).length < 1) {
            newRoom.rooms = current;
            newRoom.rooms[roomKey] = {};
            set(this.userData, newRoom);
        }
    }

    /**
     * Set the submission ID from the URL if present
     * and then set the current appstore link, which
     * is used by mailto links.
     *
     * @param props
     */
    @action setURLProps(props) {
        if (props.params && props.params.id) {
            this.submissionID = props.params.id;
            this.dataLoaded = false; // force a reload if the param is loaded explicitly
        }

        this.setLink(props);
    }

    /**
     * Used to set a url used by forms, and later used by a mailto
     *
     * @param props
     */
    @action setLink(props) {
        let link = props.url; // remove id from url

        if (props.params.id) {
            // remove the id param, we'll add back a fresh one later if its needed
            link = link.replace(props.params.id, '');
        }

        this.Link = link.endsWith('/') ? link : `${link}/`;
    }

    /**
     * Used to set recaptcha token
     *
     * @param string token
     */
    @action setCaptchaToken(token) {
        this.captchaToken = token;
    }

    /**
     * Used to set is recaptcha is verified
     *
     * @param string token
     */
    @action setRecaptchaVerified(verified) {
        this.isCaptchaVerified = verified;
    }

    /**
     * Used to set recaptcha error if verification fails
     *
     * @param string token
     */
    @action setRecaptchaErrorMessage(message) {
        this.captchaVerificationError = message;
    }

    static getInstance() {
        return AppStore.instance || (AppStore.instance = new AppStore());
    }

    /**
     * Utility fn to recursively go through a set of nested
     * field names and assemple a path to the field in dot
     * notation used by mobx-react-form
     *
     * @param fieldPath
     * @param levelUp
     */
    static getFieldPath(fieldPath, levelUp) {
        const parents = fieldPath.split('.');
        let i;

        for (i = 0; i < levelUp; i++) {
            parents.pop();
        }

        return parents.join('.');
    }

    /**
     * Get a generic error message
     *
     * @param goNext
     */
    static getError(goNext) {
        if (goNext) {
            return 'An error occurred. Please try again later.';
        }

        return 'An error occurred while saving. Your link might be missing data.';
    }

    /**
     * Get the graphql endpoint used by the app.
     *
     * The trailing slash is deliberate: UAT/prod redirect /graphql -> /graphql/,
     * and a 301/302 on a POST is re-issued as a GET with no body, dropping the
     * query so the server answers "This endpoint requires a query parameter". Hitting
     * the slashed URL directly avoids the redirect entirely.
     */
    getApiUri() {
        const location = window.location.host;

        if (
            window.location.hostname === 'localhost' ||
            window.location.hostname === '10.0.2.2'
        ) {
            return '//tenancy.test/graphql/';
        }

        return `//${location}/graphql/`;
    }

    /**
     * create the graphql client
     */
    apolloClient = new ApolloClient({
        link: new HttpLink({ uri: this.getApiUri() }),
        cache: new InMemoryCache().restore(window.__APOLLO_STATE__)
    });

    /**
     * Retrieve a submission via grapql
     * Check if we have a submission ID first, and sets the
     * appStore loading state (not the component loading state).
     */
    async retrieveSubmission() {
        this.error = false;

        if (!this.submissionID) {
            this.loading = false;
            return;
        }

        this.loading = true;

        const result = await this.apolloClient
            .query({
                query: InitialQuery,
                variables: {
                    URLID: this.submissionID
                },
                fetchPolicy: 'network-only',
                context: {
                    headers: {
                        'X-CSRF-TOKEN': this.sectoken
                    }
                }
            })
            .catch(result => {
                this.handleServerError(result, this);
                this.loading = false;
                return false;
            });

        if (
            result &&
            result.data.readSubmission &&
            result.data.readSubmission.length > 0
        ) {
            const data = result.data.readSubmission[0];

            if (data.Error) {
                this.error = JSON.parse(data.Error);
            } else {
                // as we have new submission data from the server,
                // and because mobx-persist doesnt work well with clearing items
                // we need to do this manually using localForage
                this.reset(() => {
                    this.saveRecordAsLocalData(data);
                    this.loading = false;
                });
            }
        }
    }

    /**
     * Mint a fresh reCAPTCHA v3 token for a submission write.
     *
     * Calls grecaptcha.execute() on every invocation — no token is cached or
     * reused, since v3 tokens are single-use and expire in ~2 min.
     *
     * @param action
     * @returns {Promise<string|boolean>} the token, or false if minting failed
     */
    getRecaptchaToken(action = 'hat_submit') {
        return new Promise(resolve => {
            if (!window.grecaptcha) {
                resolve(false);
                return;
            }

            window.grecaptcha.ready(() => {
                window.grecaptcha.execute(this.captchaSiteKey, { action })
                    .then(resolve)
                    .catch(() => resolve(false));
            });
        });
    }

    /**
     * Save data to the server given a graphql mutation
     *
     * @param mutation
     * @param store
     * @param form
     * @param goNext
     */
    async save(mutation, store, form, goNext) {
        // Mint a fresh reCAPTCHA v3 token per submit so the server can verify this
        // write before any DB manipulation.
        if (this.enableRecaptcha && this.captchaSiteKey) {
            const token = await this.getRecaptchaToken();

            if (!token) {
                this.handleServerError(
                    'reCAPTCHA verification failed. Please refresh and try again.',
                    store
                );
                return;
            }

            mutation.context = mutation.context || {};
            mutation.context.headers = mutation.context.headers || {};
            mutation.context.headers['X-Recaptcha-Token'] = token;
        }

        const result = await this.apolloClient
            .mutate(mutation)
            .catch(result => {
                this.handleServerError(result, store);
            });

        if (result?.data?.toolSubmissionMutation) {
            // uncomment this to use a popup to display the rescult calculator used
            // if (result?.data?.toolSubmissionMutation?.ResultCalculator) {
            //     if (confirm(`Calculator is version ${result.data.toolSubmissionMutation.ResultCalculator}`) === false) {
            //         store.loading = false;
            //         return;
            //     }
            // }

            this.handleSaveResult(result, store, form, goNext);
        }
    }

    /**
     * Handle the result from a server save.
     * - Detects errors in the payload from a successfull request (200)
     * - Sets persisted data in the appStore if a URLID is returned
     *
     * @param result
     * @param store
     * @param form
     * @param goNext
     */
    handleSaveResult(result, store, form, goNext) {
        if (result.data.toolSubmissionMutation.Error) {
            // Handle errors returned by the server. Even
            // though the response may have a 200 status,
            // the payload may contain error data.
            this.handleSaveError(result, store, form);
        } else if (result.data.toolSubmissionMutation.URLID) {
            this.submissionID = result.data.toolSubmissionMutation.URLID;

            if (result.data.toolSubmissionMutation.Era) {
                this.setEra(result.data.toolSubmissionMutation.Era);
            }

            if (result.data.toolSubmissionMutation.Zone) {
                this.setZone(result.data.toolSubmissionMutation.Zone);
            }

            if (result.data.toolSubmissionMutation.ConsentZone) {
                this.setConsentZone(result.data.toolSubmissionMutation.ConsentZone);
            }

            if (result.data.toolSubmissionMutation.ConsentZoneCompliant) {
                this.setConsentZoneCompliant(result.data.toolSubmissionMutation.ConsentZoneCompliant);
            }

            if (typeof result.data.toolSubmissionMutation.Retrofitted2009 !== typeof undefined) {
                this.setRetrofitted2009(result.data.toolSubmissionMutation.Retrofitted2009);
            }

            if (result.data.toolSubmissionMutation.Standards2009Compliant) {
                this.setStandards2009Compliant(result.data.toolSubmissionMutation.Standards2009Compliant);
            }

            if (result.data.toolSubmissionMutation.RValueEra) {
                this.setRValueEra(result.data.toolSubmissionMutation.RValueEra);
            }

            if (result.data.toolSubmissionMutation.AssumedExternalTemperature) {
                this.setAssumedExternalTemperature(
                    result.data.toolSubmissionMutation
                        .AssumedExternalTemperature
                );
            }

            if (result.data.toolSubmissionMutation.Stairwells) {
                this.setStairwells(result.data.toolSubmissionMutation.Stairwells);
            }

            store.error = false;
            store.goNext = goNext;
        } else {
            this.error = {
                code: 500,
                message: AppStore.getError(goNext)
            };
        }

        store.loading = false;
    }

    /**
     * Handle 400 codes as 'validation' messages.
     * These are shown in the form footer, and the
     * form is invalidated.
     * todo could do specifc errors here
     *
     * @param result
     * @param store
     * @param form
     */
    handleSaveError(result, store, form) {
        const error = JSON.parse(result.data.toolSubmissionMutation.Error);
        if (error.code && error.code === 400) {
            form.invalidate(error.message);
        } else {
            this.error = error;
        }
    }

    /**
     * Catch Graphgql servererror
     * this will catch any e.g. 500 server errors
     * but not any errors returned in the payload data
     * (for a 200 response)
     *
     * @param result
     * @param store
     */
    handleServerError(result, store) {
        if (result.graphQLErrors) {
            const errors = result.graphQLErrors.map(error => error.message);
            store.error = {
                message: errors.join(', '),
                code: 500
            };
        } else if (result.networkError) {
            store.error = {
                message: result.networkError,
                code: 500
            };
        } else {
            store.error = {
                message: result,
                code: 500
            };
        }

        store.loading = false;
    }

    /**
     * Save data retrieved from the backend into persisted vars
     * on appStore (they will be saved into localStorage).
     *
     * Note: appstore.reset() must have been called prior to this,
     * as mobx-persist struggles to clear data one-by-one
     *
     * @param record
     */
    saveRecordAsLocalData(record) {
        if (record.URLID) {
            this.submissionID = record.URLID;
        }

        const newData = {};

        if (record.HomeDetails) {
            newData.homedetails = JSON.parse(record.HomeDetails);
        }

        if (record.FloorArea) {
            newData.floorarea = JSON.parse(record.FloorArea);
        }

        if (record.Rooms) {
            newData.rooms = JSON.parse(record.Rooms);
        }

        this.setObservables(record);

        set(this.userData, newData);
        this.dataLoaded = true;
    }

    /**
     * Set observables in this class from provided data
     * @param record | JSON
     */
    setObservables(record) {
        this.stairwells = record.Stairwells ? record.Stairwells : this.stairwells;
        this.era = record.Era ? record.Era : this.era;
        this.zone = record.Zone ? record.Zone : this.zone;
        this.consentZone = record.ConsentZone ? record.ConsentZone : this.consentZone;
        this.consentZoneCompliant = record.ConsentZoneCompliant ? record.ConsentZoneCompliant : this.consentZoneCompliant;
        this.retrofitted2009 = record.Retrofitted2009 ? record.Retrofitted2009 : this.retrofitted2009;
        this.standards2009Compliant = record.Standards2009Compliant ? record.Standards2009Compliant : this.standards2009Compliant;
        this.rValueEra = record.RValueEra ? record.RValueEra : this.rValueEra;
        this.assumedExternalTemperature = record.AssumedExternalTemperature ? record.AssumedExternalTemperature : this.assumedExternalTemperature;
        this.priorV2Submission = record.PriorV2Submission ? record.PriorV2Submission : false;
    }

    /**
     * Rehydrate the form from the userdata
     *
     * @param data | JSON
     * @param sectionName | string - section of json/form where data stored
     *
     * @param form | An instance of a form
     */
    hydrateFormFields(data, sectionName, form) {
        let fieldName;

        for (fieldName in data) {
            if (!data.hasOwnProperty(fieldName)) {
                continue;
            }

            this.setFieldValue(fieldName, sectionName, data, form);
        }
    }

    /**
     * Recursively loop each field and subfields to set the value on each form field
     *
     * @param fieldName
     * @param parentFieldName
     * @param userData
     * @param form | An instance of a form
     */
    setFieldValue(fieldName, parentFieldName, userData, form) {
        let childFieldName;
        const currentField = parentFieldName ? `${parentFieldName}.${fieldName}` : fieldName,
            savedData = userData[fieldName];

        if (typeof savedData !== 'object') {
            form.$(currentField).set('value', savedData);
            return;
        }

        for (childFieldName in userData[fieldName]) {
            if (!userData[fieldName].hasOwnProperty(childFieldName)) {
                continue;
            }

            this.setFieldValue(
                childFieldName,
                currentField,
                userData[fieldName],
                form
            );
        }
    }

    @computed get currentLink() {
        const host = window.location.origin ? window.location.origin : window.location.protocol + '//' + window.location.host,
            link = host + this.baseURL + this.Link + this.submissionID;

        return link;
    };

    async emailLink(values, form) {
        this.emailLinkLoading = true;
        this.emailLinkSuccess = false;
        this.emailLinkError = false;

        const result = await this.apolloClient
            .mutate({
                mutation: emailLinkMutation,
                variables: {
                    URLID: this.submissionID,
                    Link: this.Link,
                    SecurityID: this.sectoken,
                    Email: values.Email
                },
                fetchPolicy: 'no-cache',
                context: {
                    headers: {
                        'X-CSRF-TOKEN': this.sectoken
                    }
                }
            })
            .catch(result => {
                this.handleEmailServerError(result);
            });

        if (result) {
            const data = result.data.toolEmailMutation;
            if (data.Error) {
                this.emailLinkError = JSON.parse(data.Error);
            } else if (result.data.toolEmailMutation.URLID) {
                this.emailLinkSuccess = values.Email;
            } else {
                this.emailLinkError = {
                    message: 'Could not send link',
                    code: 500
                };
            }

            // prevent flash of loader for fast API's
            setTimeout(() => {
                this.emailLinkLoading = false;
            }, 300);
        } else {
            this.emailLinkSuccess = false;
            this.emailLinkLoading = false;
        }
    }

    /**
     * Catch Graphgql servererror
     * this will catch any e.g. 500 server errors
     * but not any errors returned in the payload data
     * (for a 200 response)
     *
     * @param result
     * @param store
     */
    handleEmailServerError(result) {
        if (result.graphQLErrors) {
            const errors = result.graphQLErrors.map(error => error.message);
            this.emailLinkError = {
                message: errors.join(', '),
                code: 500
            };
        } else if (result.networkError) {
            this.emailLinkError = {
                message: result.networkError,
                code: 500
            };
        } else {
            this.emailLinkError = {
                message: result,
                code: 500
            };
        }

        this.emailLinkLoading = false;
    }

    /**
     * Rate
     *
     * @param values
     */
    async rate(values) {
        const result = await this.apolloClient
            .mutate({
                mutation: RatingMutation,
                variables: {
                    Rating: values.Rating,
                    Comments: values.Comments
                },
                fetchPolicy: 'no-cache',
                context: {
                    headers: {
                        'X-CSRF-TOKEN': this.sectoken
                    }
                }
            })
            .catch(result => {
                console.log('RATING ERROR', result);
                // this.handleRatingServerError(result,);
            });

        if (result && result.data && result.data.ratingMutation) {
            this.ratingSubmitted.set(result.data.ratingMutation.Rating, { expires: 1 }); // 1 day expiry
            this.ratingValue = result.data.ratingMutation.Rating;
            this.ratingComments = result.data.ratingMutation.Comments;
        }
    }
}
